/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import AdminFormPlaceholder from 'src/components/Admin/AdminFormPlaceholder';
import AdminPortfolioPropertyMemberCreate from 'src/components/Admin/AdminPortfolio/AdminPortfolioPropertyMemberCreate';
import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import NotFoundError, { areRootNotFoundErrors } from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/NotFoundError';
import Loading from 'src/components/Loading';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import getFirstPageRetryVariables from './helpers/getFirstPageRetryVariables';
import getRouteUuidRetryVariables from './helpers/getRouteUuidRetryVariables';
import getShowRouteKey from './helpers/getShowRouteKey';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'admin.admin_portfolio.admin_portfolio_property_member_create.page.initial_load_error.message',
    defaultMessage: 'An error occurred while loading this portfolio. Please try again.',
  },
  notFoundTitle: {
    id: 'admin.admin_portfolio.admin_portfolio_property_member_create.page.not_found.title',
    defaultMessage: 'Portfolio not found',
  },
});

export const adminPortfolioPropertyMemberCreateRouteQuery = graphql`
  query AdminPortfolioPropertyMemberCreateRoute_Query(
    $uuid: ID!
    $count: Int
    $cursor: String
  ) {
    viewer {
      ...AdminPortfolioPropertyMemberCreateProperties_viewer @arguments(
        count: $count,
        cursor: $cursor
      )
    }
    # Caught so a partial response, where the viewer loads, keeps the portfolio's own error.
    portfolio(uuid: $uuid) @catch {
      ...AdminPortfolioPropertyMemberCreate_portfolio
    }
  }
`;

/**
 * Get retry variables for the property member create route: the portfolio uuid
 * plus a fresh first page of selectable properties, matching the initial load.
 * @param {object} routeProps - Found-relay route props.
 * @returns {{uuid: string, count: number}|null} Retry variables, or null when the
 *   route has no portfolio uuid.
 */
function getAdminPortfolioPropertyMemberCreateRetryVariables(routeProps) {
  const uuidVariables = getRouteUuidRetryVariables(routeProps);
  return uuidVariables && { ...uuidVariables, ...getFirstPageRetryVariables() };
}

/**
 * Unwraps the route query's caught `portfolio` result for
 * `AdminPortfolioPropertyMemberCreate`. The query also loads the viewer, so an
 * unknown or failing portfolio arrives as a partial response whose error Relay
 * would otherwise drop. A root `NOT_FOUND` error throws a `NotFoundError`; any
 * other portfolio error throws to the retry modal.
 * @param {object} props - Route query data and route props.
 * @param {{ok: boolean, value: ?object, errors: ?Array<object>}|null} props.portfolio - Caught
 *   portfolio result.
 * @returns {React.ReactElement} AdminPortfolioPropertyMemberCreate with the portfolio fragment.
 * @throws {NotFoundError} When the API reports the portfolio as not found.
 * @throws {Error} When the portfolio failed to load for another reason.
 */
function AdminPortfolioPropertyMemberCreateContent({
  portfolio: portfolioResult = null, ...props
}) {
  if (portfolioResult?.ok === false) {
    if (areRootNotFoundErrors(portfolioResult.errors, 0)) {
      throw new NotFoundError('AdminPortfolioPropertyMemberCreate: the API returned no portfolio');
    }
    throw new Error('AdminPortfolioPropertyMemberCreate: the portfolio failed to load');
  }
  return (
    <AdminPortfolioPropertyMemberCreate {...props} portfolio={portfolioResult?.value ?? null} />
  );
}

AdminPortfolioPropertyMemberCreateContent.propTypes = {
  portfolio: PropTypes.shape({
    ok: PropTypes.bool.isRequired,
    value: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    errors: PropTypes.arrayOf(PropTypes.object), // eslint-disable-line react/forbid-prop-types
  }),
};

/**
 * Renders `AdminPortfolioPropertyMemberCreate` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} admin portfolio property member create route content.
 */
function AdminPortfolioPropertyMemberCreatePreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(adminPortfolioPropertyMemberCreateRouteQuery, queryRef);

  return <AdminPortfolioPropertyMemberCreateContent {...routeProps} {...queryData} />;
}

AdminPortfolioPropertyMemberCreatePreloadedQueryRenderer.propTypes = {
  routeProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `AdminPortfolioPropertyMemberCreate` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state. Nested inside the
 * `AdminLayout` boundary, which covers admin-shell errors.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} admin portfolio property member create route content.
 */
function AdminPortfolioPropertyMemberCreateRouteRenderer({ routeProps, routeError = null }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    adminPortfolioPropertyMemberCreateRouteQuery,
    routeProps,
    {
      logLabel: 'AdminPortfolioPropertyMemberCreateRoute',
      getRetryVariablesFunc: getAdminPortfolioPropertyMemberCreateRetryVariables,
    },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      notFound={{ title: intl.formatMessage(messages.notFoundTitle), backPath: '/admin/portfolios' }}
      placeholderComponent={AdminFormPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <AdminPortfolioPropertyMemberCreatePreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <AdminPortfolioPropertyMemberCreateContent {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

AdminPortfolioPropertyMemberCreateRouteRenderer.propTypes = {
  routeProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the AdminPortfolioPropertyMemberCreate route state supplied by found-relay.
 * A network error is forwarded to the renderer's `RouteErrorThrower` bridge so
 * the local error boundary can catch it. The renderer is keyed with
 * `getShowRouteKey` (uuid and non-locale query params), so any navigation
 * remounts it, clearing the selected property and dropping a retried query reference
 * that would otherwise hide a later route error. Authentication is resolved by
 * the owning `AdminLayout` route, not here.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} AdminPortfolioPropertyMemberCreate route content or loading state.
 */
export function adminPortfolioPropertyMemberCreateRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <AdminPortfolioPropertyMemberCreateRouteRenderer
        key={getShowRouteKey(routeProps)}
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <AdminPortfolioPropertyMemberCreateRouteRenderer
      key={getShowRouteKey(props)}
      routeProps={props}
    />
  );
}

export default AdminPortfolioPropertyMemberCreateRouteRenderer;
